import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, CheckCircle2, ChevronsUpDown, Loader2, XCircle } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/auth/use-auth";
import { apiFetch } from "@/lib/auth-api";
import { ensureElementVisible } from "@/lib/route-guards";

export const Route = createFileRoute("/_auth/konfiguracja/ai")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(context.queryClient, "sidebar.system.ai-config", location.pathname);
	},
	component: AiConfigPage,
});

interface AiConfig {
	provider: string;
	api_key: string;
	model: string;
	reasoning_effort?: string;
	searxngUrl?: string;
}

interface SettingsResponse {
	key: string;
	value: AiConfig | null;
}

interface SaveResponse {
	saved: boolean;
	value: AiConfig;
}

interface TestResult {
	success: boolean;
	latencyMs: number | null;
	error: string | null;
}

interface AiModel {
	id: string;
}

function isReasoningModel(model: string): boolean {
	return (
		model.startsWith("o1") ||
		model.startsWith("o3") ||
		model.startsWith("o4") ||
		model.startsWith("gpt-5")
	);
}

function AiConfigPage() {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const [provider, setProvider] = useState("openai");
	const [apiKey, setApiKey] = useState("");
	const [model, setModel] = useState("gpt-4.1-mini");
	const [reasoningEffort, setReasoningEffort] = useState("low");
	const [searxngUrl, setSearxngUrl] = useState("");
	const [initialized, setInitialized] = useState(false);

	const isSuperAdmin = user?.roles?.includes("super_admin");

	const settingsQuery = useQuery({
		queryKey: ["settings", "ai_config"],
		queryFn: () => apiFetch<SettingsResponse>("/admin/settings/ai_config"),
		enabled: !!isSuperAdmin,
	});

	if (settingsQuery.data?.value && !initialized) {
		const val = settingsQuery.data.value;
		if (val.provider) setProvider(val.provider);
		if (val.model) setModel(val.model);
		if (val.reasoning_effort) setReasoningEffort(val.reasoning_effort);
		if (val.searxngUrl) setSearxngUrl(val.searxngUrl);
		setInitialized(true);
	}

	const modelsQuery = useQuery({
		queryKey: ["settings", "ai_models"],
		queryFn: () => apiFetch<AiModel[]>("/admin/settings/ai/models"),
		enabled: !!isSuperAdmin && !!settingsQuery.data?.value?.api_key,
		staleTime: 5 * 60 * 1000,
	});

	// Auto-save: saves current config without api_key (preserves existing encrypted key)
	const autoSave = useCallback(
		(
			overrides: Partial<{
				provider: string;
				model: string;
				reasoning_effort: string;
				searxngUrl: string;
			}>,
		) => {
			const payload: Record<string, string> = {
				provider: overrides.provider ?? provider,
				model: overrides.model ?? model,
				reasoning_effort: overrides.reasoning_effort ?? reasoningEffort,
				searxngUrl: overrides.searxngUrl ?? searxngUrl,
			};
			apiFetch<SaveResponse>("/admin/settings/ai_config", {
				method: "PUT",
				body: JSON.stringify({ value: payload, encrypted: true }),
			}).then(() => {
				queryClient.invalidateQueries({ queryKey: ["settings", "ai_config"] });
			});
		},
		[provider, model, reasoningEffort, searxngUrl, queryClient],
	);

	// Save API key mutation (explicit button)
	const saveKeyMutation = useMutation({
		mutationFn: () => {
			const payload: Record<string, string> = {
				provider,
				model,
				reasoning_effort: reasoningEffort,
				searxngUrl,
				api_key: apiKey,
			};
			return apiFetch<SaveResponse>("/admin/settings/ai_config", {
				method: "PUT",
				body: JSON.stringify({ value: payload, encrypted: true }),
			});
		},
		onSuccess: () => {
			setApiKey("");
			queryClient.invalidateQueries({ queryKey: ["settings", "ai_config"] });
			queryClient.invalidateQueries({ queryKey: ["settings", "ai_models"] });
		},
	});

	const testMutation = useMutation({
		mutationFn: () => apiFetch<TestResult>("/admin/settings/ai/test", { method: "POST" }),
	});

	if (!isSuperAdmin) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">Brak dostepu. Wymagana rola super_admin.</p>
			</div>
		);
	}

	const availableModels = modelsQuery.data ?? [];
	const hasApiKey = !!settingsQuery.data?.value?.api_key;

	return (
		<div className="p-6 max-w-2xl max-md:p-3">
			<h1 className="text-2xl font-semibold mb-6 max-md:hidden">Konfiguracja AI</h1>

			<Card>
				<CardHeader>
					<CardTitle>Provider AI</CardTitle>
					<CardDescription>
						Konfiguracja dostawcy AI dla wzbogacania produktow i wyszukiwania semantycznego.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="provider">Provider</Label>
						<Select
							value={provider}
							onValueChange={(v) => {
								setProvider(v);
								autoSave({ provider: v });
							}}
						>
							<SelectTrigger id="provider">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="openai">OpenAI</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-2">
						<Label htmlFor="apiKey">API Key</Label>
						<div className="flex gap-2">
							<Input
								id="apiKey"
								type="password"
								value={apiKey}
								onChange={(e) => setApiKey(e.target.value)}
								placeholder={hasApiKey ? "Wpisz nowy klucz..." : "sk-..."}
								className="flex-1"
							/>
							<Button
								onClick={() => saveKeyMutation.mutate()}
								disabled={saveKeyMutation.isPending || !apiKey}
								size="sm"
								className="max-md:h-9"
							>
								{saveKeyMutation.isPending ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									"Zapisz klucz"
								)}
							</Button>
						</div>
						{hasApiKey && (
							<p className="text-xs text-muted-foreground">
								Zapisany: {settingsQuery.data?.value?.api_key}
							</p>
						)}
						{saveKeyMutation.isSuccess && (
							<p className="text-xs text-[var(--success)] flex items-center gap-1">
								<CheckCircle2 className="h-3 w-3" />
								Klucz zapisany
							</p>
						)}
					</div>

					<div className="space-y-2">
						<Label>Model</Label>
						<ModelCombobox
							value={model}
							onChange={(v) => {
								setModel(v);
								autoSave({ model: v });
							}}
							models={availableModels}
							isLoading={modelsQuery.isLoading}
						/>
					</div>

					{isReasoningModel(model) && (
						<div className="space-y-2">
							<Label htmlFor="reasoningEffort">Poziom reasoningu</Label>
							<Select
								value={reasoningEffort}
								onValueChange={(v) => {
									setReasoningEffort(v);
									autoSave({ reasoning_effort: v });
								}}
							>
								<SelectTrigger id="reasoningEffort">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="low">Low — szybki, tani</SelectItem>
									<SelectItem value="medium">Medium — zbalansowany</SelectItem>
									<SelectItem value="high">High — dokladny, wolniejszy</SelectItem>
								</SelectContent>
							</Select>
							<p className="text-xs text-muted-foreground">
								Modele reasoning (o1, o3, o4, GPT-5) nie uzywaja temperatury — zamiast tego
								ustawiasz poziom reasoningu.
							</p>
						</div>
					)}

					<div className="space-y-2">
						<Label htmlFor="searxngUrl">SearXNG URL</Label>
						<Input
							id="searxngUrl"
							value={searxngUrl}
							onChange={(e) => setSearxngUrl(e.target.value)}
							onBlur={() => autoSave({ searxngUrl })}
							placeholder="http://host:8088"
						/>
						<p className="text-xs text-muted-foreground">
							Wyszukiwarka źródeł dla ulotek i opisów produktów.
						</p>
					</div>

					<div className="pt-2">
						<Button
							variant="outline"
							onClick={() => testMutation.mutate()}
							disabled={testMutation.isPending || !hasApiKey}
							className="max-md:h-11 max-md:w-full"
						>
							{testMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							Testuj polaczenie
						</Button>
					</div>

					{testMutation.data && (
						<div className="mt-2">
							{testMutation.data.success ? (
								<p className="text-sm text-[var(--success)] flex items-center gap-1">
									<CheckCircle2 className="h-4 w-4" />
									Polaczono ({testMutation.data.latencyMs}ms)
								</p>
							) : (
								<p className="text-sm text-destructive flex items-center gap-1 max-md:items-start max-md:break-words">
									<XCircle className="h-4 w-4 shrink-0" />
									Blad: {testMutation.data.error}
								</p>
							)}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Quality Test */}
			<QualityTestSection hasApiKey={hasApiKey} />
		</div>
	);
}

// ─── Quality Test ───────────────────────────────────────────────────────────

interface QualityTestTag {
	path: string;
	confidence: number;
}

interface QualityTestProduct {
	kod07: string;
	nazwa: string;
	description: string;
	tags: QualityTestTag[];
	latencyMs: number;
	tokensIn: number;
	tokensOut: number;
}

interface QualityTestResult {
	products: QualityTestProduct[];
	model: string;
	avgLatencyMs: number;
	avgTokensPerProduct: number;
}

function QualityTestSection({ hasApiKey }: { hasApiKey: boolean }) {
	const mutation = useMutation({
		mutationFn: () =>
			apiFetch<QualityTestResult>("/admin/settings/ai/quality-test", { method: "POST" }),
	});

	const result = mutation.data;

	return (
		<Card>
			<CardHeader>
				<CardTitle>Test jakosci modelu</CardTitle>
				<CardDescription>
					Przetestuj jak wybrany model radzi sobie z kategoryzacja produktow farmaceutycznych.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<Button
					onClick={() => mutation.mutate()}
					disabled={mutation.isPending || !hasApiKey}
					className="max-md:h-11 max-md:w-full"
				>
					{mutation.isPending ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Testowanie (5 produktow)...
						</>
					) : (
						"Uruchom test (5 produktow)"
					)}
				</Button>

				{mutation.isError && (
					<p className="text-sm text-destructive flex items-center gap-1 max-md:items-start max-md:break-words">
						<XCircle className="h-4 w-4 shrink-0" />
						Blad: {(mutation.error as Error).message}
					</p>
				)}

				{result && (
					<div className="space-y-3">
						<div className="flex items-center gap-4 text-xs text-muted-foreground border-b border-border pb-2 max-md:flex-wrap max-md:gap-x-4 max-md:gap-y-1">
							<span>
								Model: <span className="font-medium text-foreground">{result.model}</span>
							</span>
							<span>
								Sr. czas:{" "}
								<span className="font-medium text-foreground">{result.avgLatencyMs}ms</span>
							</span>
							<span>
								Sr. tokeny/prod:{" "}
								<span className="font-medium text-foreground">~{result.avgTokensPerProduct}</span>
							</span>
						</div>

						{result.products.map((p) => (
							<div key={p.kod07} className="rounded-lg border border-border p-3 space-y-2">
								<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-1">
									<div className="max-md:min-w-0 max-md:break-words">
										<span className="text-sm font-medium">{p.nazwa}</span>
										<span className="ml-2 text-xs font-mono text-muted-foreground">{p.kod07}</span>
									</div>
									<span className="text-xs text-muted-foreground">
										{p.latencyMs}ms · {p.tokensIn + p.tokensOut} tok
									</span>
								</div>
								<p className="text-sm text-foreground leading-relaxed bg-muted/30 rounded p-2 max-md:break-words">
									{p.description}
								</p>
								<div className="flex flex-wrap gap-1">
									{p.tags.map((t) => (
										<span
											key={t.path}
											className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs"
										>
											{t.path.split(":").pop()}
											<span className="text-muted-foreground">
												{Math.round(t.confidence * 100)}%
											</span>
										</span>
									))}
								</div>
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

// ─── Model Combobox ──────────────────────────────────────────────────────────

function ModelCombobox({
	value,
	onChange,
	models,
	isLoading,
}: {
	value: string;
	onChange: (v: string) => void;
	models: AiModel[];
	isLoading: boolean;
}) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const filtered = useMemo(() => {
		if (!search) return models;
		const q = search.toLowerCase();
		return models.filter((m) => m.id.toLowerCase().includes(q));
	}, [models, search]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className="w-full justify-between font-normal"
					onClick={() => {
						setOpen(true);
						setTimeout(() => inputRef.current?.focus(), 50);
					}}
				>
					{value || "Wybierz model..."}
					{isLoading ? (
						<Loader2 className="ml-2 h-4 w-4 animate-spin shrink-0" />
					) : (
						<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
				<div className="p-2">
					<Input
						ref={inputRef}
						placeholder="Szukaj modelu..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="h-8 max-md:h-9"
					/>
				</div>
				<div className="max-h-60 overflow-y-auto">
					{filtered.length === 0 ? (
						<p className="py-4 text-center text-sm text-muted-foreground">
							{models.length === 0 ? "Zapisz API key, aby pobrac liste modeli" : "Brak wynikow"}
						</p>
					) : (
						filtered.map((m) => (
							<button
								type="button"
								key={m.id}
								className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent cursor-pointer max-md:min-h-9 max-md:text-left max-md:break-all"
								onClick={() => {
									onChange(m.id);
									setOpen(false);
									setSearch("");
								}}
							>
								<Check
									className={`h-4 w-4 shrink-0 ${value === m.id ? "opacity-100" : "opacity-0"}`}
								/>
								{m.id}
							</button>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
